import { Form } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useLoading } from 'react-simplikit';

import { Flex, Space, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import Modal, { useModalContext } from '@/components/Modal/Modal';
import Typo, { Span } from '@/components/Typography/Typo';
import { getApiErrorMessage } from '@/error-handler/api-error-messages/ApiErrorMessages';
import { ApiError } from '@/error-handler/ApiError';
import {
  CLIENT_SIDE_ERROR_CODE,
  ClientError,
} from '@/error-handler/ClientError';
import DnDFileUploader from '@/features/CsvImport/DnDFileUploader';
import { DownloadSampleCSV } from '@/features/CsvImport/DownloadSampleCSV';
import FieldTable from '@/features/CsvImport/FieldTable';
import service from '@/services';
import { popup } from '@/stores/pure/PopupStore';
import { toast } from '@/stores/pure/ToastStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { LANGUAGE, SCHEDULE_PERIOD, WEEKDAY } from '@/types/commonConst';
import { getFeedbackSourceName } from '@/types/feedbackConst';
import { filterMap } from '@/utils/lazy';

import type { CreateScrapingJobModel, ValidateScrapingJobsResponse } from '../api';
import { CSVPreview } from '../components/CSVPreview';
import { AdminConfirmField } from '../components/Fields/AdminConfirmField';
import { ItemPerDayField } from '../components/Fields/ItemPerDayField';
import { MaxItemsField } from '../components/Fields/MaxItemsField';
import { ScheduleField } from '../components/Fields/ScheduleField';
import { ScraperOptionField } from '../components/Fields/ScraperOptionField';
import { SourceTypeField } from '../components/Fields/SourceTypeField';
import {
  getFieldProperty,
  getIsItemPerDayField,
  getMaxItemCountByScraper,
  getScraperFieldList,
} from '../configs/config-actions';
import { SCRAPER_REQ_FIELD_KEY } from '../configs/scraper-field-map/field-const';
import { useCreateProducts } from '../hooks/useCreateProduct';
import { useInitialSource } from '../hooks/useInitalSource';
import {
  type DraftScrapingJob,
  getScrapingJobTypeIcon,
  type ScraperFieldInput,
  type ScraperInputObj,
  SCRAPING_JOB_STATUS,
  SCRAPING_JOB_TYPE,
} from '../types';
import { parseCSVData } from '../utils/parseCSVData';
import { validateMaxItems } from '../utils/validateMaxItemsValue';

enum MODE {
  SETTING = 'SETTING',
  PREVIEW = 'PREVIEW',
}

export const ScrapingCSVUploadModal = (props: {
  onSuccess: (type: SCRAPING_JOB_TYPE) => void;
  jobType: SCRAPING_JOB_TYPE;
}) => {
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const { createMultipleProducts } = useCreateProducts();
  const { closeModal } = useModalContext();

  const [form] = Form.useForm<DraftScrapingJob>();
  const source = Form.useWatch('source_type', form);
  const scraper = Form.useWatch('scraper_name', form);
  const schedule = Form.useWatch('schedule', form);
  const start_date = Form.useWatch('start_date', form);
  const end_date = Form.useWatch('end_date', form);
  const shift_days = Form.useWatch('shift_days', form);
  const item_per_day = Form.useWatch('item_per_day', form);
  const max_items = Form.useWatch('max_items', form);

  const { sourceOptions, scraperOptions, currentScraperOption } =
    useInitialSource(form);

  const [mode, setMode] = useState<MODE>(MODE.SETTING);
  const [dragActive, setDragActive] = useState(false);
  const [errors, setErrors] = useState<ValidateScrapingJobsResponse>([]);
  const [parsedData, setParsedData] = useState<ScraperInputObj[]>([]);
  const [loading, startLoading] = useLoading();

  const displayColumns = useMemo(() => {
    if (!currentScraperOption) return [];
    const fieldList = [
      SCRAPER_REQ_FIELD_KEY.TITLE,
      ...getScraperFieldList(currentScraperOption),
    ];
    return filterMap(fieldList, (field) => {
      const properties = getFieldProperty(field, currentScraperOption);
      if (!properties) return null;
      return {
        columnKey: field,
        type: properties?.type,
        required: properties?.required,
        desc: properties?.desc,
      };
    });
  }, [currentScraperOption]);

  const uploadData = async (data: Record<SCRAPER_REQ_FIELD_KEY, string>[]) => {
    if (!currentScraperOption) return;
    const fieldList = getScraperFieldList(currentScraperOption);
    try {
      const parsedData = parseCSVData(data, fieldList, currentScraperOption);
      setParsedData(parsedData);
      setMode(MODE.PREVIEW);
    } catch (e) {
      console.error(e);
      if (e instanceof ClientError) {
        popup({
          popupTheme: 'negative',
          type: 'alert',
          title: e.client_error_code,
          message: e.message,
          confirmText: 'Close',
        });
      } else {
        popup({
          popupTheme: 'negative',
          type: 'alert',
          title: CLIENT_SIDE_ERROR_CODE.CSV_UPLOAD_ERROR,
          message: 'Unknown error',
          confirmText: 'Close',
        });
      }
      return;
    }
  };

  const isItemPerDay = getIsItemPerDayField(currentScraperOption);
  const defaultMaxItems = getMaxItemCountByScraper(currentScraperOption);

  const mappedData: CreateScrapingJobModel[] = useMemo(() => {
    const commonFields: Omit<
      CreateScrapingJobModel,
      'title' | 'scraper_input'
    > = {
      source_type: source,
      scraper_name: scraper,
      type: props.jobType,
    };
    if (props.jobType === SCRAPING_JOB_TYPE.SCHEDULED) {
      commonFields.schedule = schedule;
      commonFields.shift_days = shift_days;
    } else {
      commonFields.start_date = start_date;
      commonFields.end_date = end_date;
    }

    if (isItemPerDay) {
      commonFields.item_per_day = validateMaxItems(item_per_day);
    } else {
      commonFields.max_items = validateMaxItems(max_items, defaultMaxItems);
    }

    const itemRequestLimitObject = isItemPerDay
      ? {
          item_per_day: validateMaxItems(item_per_day),
        }
      : {
          max_items: validateMaxItems(max_items, defaultMaxItems),
        };

    return parsedData.map((row) => {
      const newRow = { ...row };
      const title = newRow.title
        ? (newRow.title as string)
        : getFeedbackSourceName(source);
      if (newRow.title) {
        delete newRow.title;
      }
      const rowData = { ...newRow } as unknown as ScraperFieldInput;

      // 스크래퍼 타입에 따라 적절한 필드 사용
      return {
        ...commonFields,
        title: title,
        scraper_input: {
          ...rowData,
          ...itemRequestLimitObject,
          language: currWorkspace?.language || LANGUAGE.EN,
        },
      };
    });
  }, [
    source,
    scraper,
    props.jobType,
    parsedData,
    schedule,
    shift_days,
    start_date,
    end_date,
    item_per_day,
    max_items,
    currWorkspace?.language,
    isItemPerDay,
    defaultMaxItems,
  ]);

  const validateData = useCallback(async () => {
    if (props.jobType === SCRAPING_JOB_TYPE.SCHEDULED) {
      if (!schedule?.period) {
        popup({
          popupTheme: 'negative',
          type: 'alert',
          title: 'Validation failed',
          message: 'Schedule is required',
        });
        return;
      }
    } else {
      if (
        !form.getFieldValue('start_date') ||
        !form.getFieldValue('end_date')
      ) {
        popup({
          popupTheme: 'negative',
          type: 'alert',
          title: 'Validation failed',
          message: 'Start date and end date are required',
        });
      }
    }
    const need_confirm = form.getFieldValue('need_confirm');
    const errors = await service.scraping.validateScrapingJob({
      jobs: mappedData,
      need_confirm: need_confirm,
    });
    setErrors(errors);
    if (errors.length > 0) {
      popup({
        title: 'Validation failed',
        message: errors.map((error) => {
          return (
            <div key={error.row_index} className="flex flex-col gap-2">
              <Typo type="body2" className="text-gray-700">
                [Row : {error.row_index}]
              </Typo>
              {error.field_errors.map((error) => {
                return (
                  <Typo
                    key={error.cause}
                    type="body3"
                    className="text-destructive"
                  >
                    [{error.related_fields.join(', ')}] : {error.cause}
                  </Typo>
                );
              })}
            </div>
          );
        }),
        type: 'alert',
        popupTheme: 'negative',
      });
    } else {
      toast('Validation passed', 'success');
    }
  }, [form, mappedData, props.jobType, schedule]);

  const onSubmit = async () => {
    const submitLogic = async () => {
      try {
        const need_confirm = form.getFieldValue('need_confirm');
        const errors = await service.scraping.validateScrapingJob({
          jobs: mappedData,
          need_confirm: need_confirm,
        });
        if (errors.length > 0) {
          toast('Validation failed', 'fail');
          return;
        }
        await service.scraping.createScrapingJob({
          jobs: mappedData,
          need_confirm: need_confirm,
        });

        await createMultipleProducts(
          mappedData.map((d) => {
            return {
              product_brand_name: d.scraper_input.brand,
              product_name: d.scraper_input.product_name,
              product_line_name: d.scraper_input.product_line,
            };
          }),
        );
      } catch (error) {
        if (error instanceof ApiError) {
          if (
            error.data.client_code === 'scraping_job__exist_in_progress_job'
          ) {
            toast(getApiErrorMessage(error.data.client_code), 'fail');
            throw error;
          }
        }
        throw error;
      }
    };

    await startLoading(submitLogic());

    toast('Scraping job created', 'success');
    closeModal();

    props.onSuccess(props.jobType);
  };

  const JobIcon = getScrapingJobTypeIcon(props.jobType);

  return (
    <Modal.Layout modalSize="l">
      <Modal.Header>
        <Flex a="center" g={8}>
          <JobIcon size={20} />
          <Span size={0} weight="sb">
            {props.jobType === SCRAPING_JOB_TYPE.ONETIME
              ? 'Create bulk jobs'
              : 'Create bulk schedules'}
          </Span>
        </Flex>
      </Modal.Header>

      <Modal.Body
        style={{
          maxHeight: 'calc(100vh - 200px)',
        }}
      >
        <Form
          form={form}
          layout="horizontal"
          labelAlign="left"
          labelCol={{ prefixCls: 'w-[140px]' }}
          wrapperCol={{ flex: 1 }}
          initialValues={{
            status: SCRAPING_JOB_STATUS.BEFORE_SCRAPING,
            schedule: {
              period: SCHEDULE_PERIOD.WEEKLY,
              hour: 0,
              weekday: WEEKDAY.SUNDAY,
            },
            shift_days: undefined,
          }}
          validateTrigger="onChange"
        >
          <VFlex>
            <SourceTypeField
              layout="vertical"
              sourceOptions={sourceOptions}
              disabled={mode === MODE.PREVIEW}
            />
            <ScraperOptionField
              scraperOptions={scraperOptions}
              disabled={mode === MODE.PREVIEW}
            />
            <ScheduleField type={props.jobType} />
            {currentScraperOption &&
              (isItemPerDay ? (
                <ItemPerDayField
                  maxCount={getMaxItemCountByScraper(currentScraperOption)}
                />
              ) : (
                <MaxItemsField
                  maxCount={getMaxItemCountByScraper(currentScraperOption)}
                />
              ))}
            <AdminConfirmField />
          </VFlex>
        </Form>
        {mode === MODE.SETTING && currentScraperOption && (
          <>
            <DownloadSampleCSV
              description={
                <>
                  You can upload your scraping request using a csv.
                  <br />
                  See the table below for information on the scraping request
                  fields you can upload.
                </>
              }
              href={`/scraping-csv-sample/${currentScraperOption}.csv`}
              fileName={`${currentScraperOption}_sample.csv`}
            />

            <Space h={24} />

            <DnDFileUploader
              uploadData={uploadData}
              dragActive={dragActive}
              setDragActive={setDragActive}
            />
            <Space h={24} />
            <FieldTable rows={displayColumns} />
          </>
        )}

        {mode === MODE.PREVIEW && currentScraperOption && (
          <CSVPreview
            tempData={parsedData}
            setTempData={setParsedData}
            errors={errors}
            scraperName={currentScraperOption}
          />
        )}
      </Modal.Body>
      <Modal.Footer
        additional={
          mode === MODE.PREVIEW
            ? {
                sizeType: 'm',
                variant: 'ghost',
                children: 'Back',
                onClick: () => setMode(MODE.SETTING),
              }
            : undefined
        }
      >
        <Flex g={8}>
          <Button sizeType="m" variant="ghost" onClick={closeModal}>
            Close
          </Button>

          <Button
            sizeType="m"
            variant="secondary"
            onClick={validateData}
            disabled={mode === MODE.SETTING}
          >
            Validate
          </Button>
          <Button
            sizeType="m"
            variant="primary"
            onClick={onSubmit}
            isLoading={loading}
            disabled={mode === MODE.SETTING || errors.length > 0}
          >
            Submit
          </Button>
        </Flex>
      </Modal.Footer>
    </Modal.Layout>
  );
};
